import axios from 'axios';
import type { BeneficiaryType, SubmitterSourceType } from './leadSigningService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

export type { BeneficiaryType, SubmitterSourceType };

// ============================================================
// פרטי חתימה — שדות משותפים ל-ClientDto / CreateClientDto / UpdateClientDto.
// השדות אופציונליים (לקוחות ידניים יכולים להישאר ריקים), הדגלים boolean ולא optional כי בצד שרת NOT NULL.
// ============================================================
export interface ClientSigningDetailsFields {
  beneficiaryType?: BeneficiaryType;
  // הורים
  fatherName?: string;
  fatherIdNumber?: string;
  fatherAddress?: string;
  fatherCity?: string;
  fatherBirthDate?: string;
  isFatherForeignResident: boolean;
  motherName?: string;
  motherIdNumber?: string;
  motherAddress?: string;
  motherCity?: string;
  motherBirthDate?: string;
  isMotherForeignResident: boolean;
  // בן/בת זוג
  spouseName?: string;
  spouseAddress?: string;
  spouseCity?: string;
  spouseIdNumber?: string;
  spouseBirthDate?: string;
  isSpouseForeignResident: boolean;
  // מגיש הבקשה
  submitterSourceType?: SubmitterSourceType;
  submitterName?: string;
  submitterRelationship?: string;
  submitterIdNumber?: string;
  submitterAddress?: string;
  submitterCity?: string;
}

export const emptyClientSigningDetails = (): ClientSigningDetailsFields => ({
  isFatherForeignResident: false,
  isMotherForeignResident: false,
  isSpouseForeignResident: false,
});

// Contact DTOs (טלפון + אימייל אופציונלי)
export interface ContactDto {
  id: number;
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder: number;
}

export interface CreateContactDto {
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder?: number;
}

export interface UpdateContactDto {
  id: number;
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder: number;
}

export interface ClientMedicalInfoDto {
  id: number;
  clientId: number;
  diseaseId: number;
  diseaseName: string;
  diseaseCategoryName: string;
  isPrimary: boolean;
  diagnosisDate?: string;
  notes?: string;
}

export interface ClientCurrentStageDto {
  statusId: number;
  statusName: string;
  statusColor?: string;
  sortOrder: number;
  stageDate?: string;
}

export interface ClientDto extends ClientSigningDetailsFields {
  id: number;
  originalLeadId?: number;
  contractId?: number;
  fullName: string;
  idNumber: string;
  isForeignResident: boolean; // האם תושב חו"ל
  phone: string;
  email?: string;
  contacts: ContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  assignedCaseManagerId?: number;
  assignedCaseManagerName?: string;
  status: string;
  currentStatusId?: number;
  currentStatusName?: string;
  currentStatusColor?: string;
  lastStatusChangeAt?: string;
  // רשימת הסטטוסים הפעילים (Current) בציר הזמן של הלקוח, ממוינת לפי sortOrder
  currentStages?: ClientCurrentStageDto[];
  treatmentStartDate?: string;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
  salesRepName?: string;
  contractSignedDate?: string;
  commissionPercentage?: number;
  benefitType?: number;
  benefitTypeName?: string;
  address?: string;
  city?: string;
  birthDate?: string; // תאריך לידה
  medicalInfo: ClientMedicalInfoDto[];
  primaryDiseaseName?: string;
  daysInTreatment: number;
  wasHospitalizedLastYear: boolean;
  hadSurgeryLastYear: boolean;
  submittedClaimBefore: boolean;
  nationalInsuranceUsername?: string;
  hasNationalInsurancePassword: boolean;
  lastNationalInsuranceCheck?: string;
  // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
  beneficiaryName?: string;
  beneficiaryIdNumber?: string;
  bankCode?: string;
  branchNumber?: string;
  accountNumber?: string;
  // פרטי משלם הקצבה
  payerName?: string;
  payerIdNumber?: string;
  isPayerForeignResident?: boolean;
}

export interface ClientFilterDto {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  assignedCaseManagerId?: number;
  statusIds?: number[];
  diseaseIds?: number[];
  dateFrom?: string;
  dateTo?: string;
  unassignedOnly?: boolean;
  assignedOnly?: boolean;
  sortBy?: string;
  sortDescending?: boolean;
}

export interface ClientPagedResultDto {
  clients: ClientDto[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface ClientAssignmentDto {
  clientId: number;
  caseManagerId: number;
}

export interface BulkClientAssignmentDto {
  clientIds: number[];
  caseManagerId: number;
}

export interface ClientTransferDto {
  clientId: number;
  newCaseManagerId: number;
  transferReason: string;
}

export interface ClientAssignmentResultDto {
  success: boolean;
  successCount: number;
  errors: string[];
}

export interface UpdateClientDto extends ClientSigningDetailsFields {
  fullName: string;
  idNumber: string;
  isForeignResident?: boolean; // האם תושב חו"ל
  phone: string;
  email?: string;
  contacts?: UpdateContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  commissionPercentage?: number;
  benefitType?: number;
  address?: string;
  city?: string;
  birthDate?: string; // תאריך לידה
  wasHospitalizedLastYear: boolean;
  hadSurgeryLastYear: boolean;
  submittedClaimBefore: boolean;
  nationalInsuranceUsername?: string;
  nationalInsurancePassword?: string;
  clearNationalInsurancePassword?: boolean; // Set to true to delete the password
  // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
  beneficiaryName?: string;
  beneficiaryIdNumber?: string;
  bankCode?: string;
  branchNumber?: string;
  accountNumber?: string;
  // פרטי משלם הקצבה
  payerName?: string;
  payerIdNumber?: string;
  isPayerForeignResident?: boolean;
}

export interface CreateClientDto extends ClientSigningDetailsFields {
  fullName: string;
  idNumber: string;
  isForeignResident?: boolean; // האם תושב חו"ל
  phone: string;
  email?: string;
  contacts?: CreateContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  assignedCaseManagerId?: number;
  commissionPercentage: number; // ברירת מחדל 20%
  benefitType?: number;
  address?: string;
  city?: string;
  // אישור שהכתובת תואמת לכתובת במשרד הפנים — נדרש כאשר address/city מולאו
  addressConfirmed?: boolean;
  birthDate?: string; // תאריך לידה
  wasHospitalizedLastYear: boolean;
  hadSurgeryLastYear: boolean;
  submittedClaimBefore: boolean;
  nationalInsuranceUsername?: string;
  nationalInsurancePassword?: string;
  // פרטי חשבון בנק להעברת קצבה - פרטי מקבל הקצבה
  beneficiaryName?: string;
  beneficiaryIdNumber?: string;
  bankCode?: string;
  branchNumber?: string;
  accountNumber?: string;
  // פרטי משלם הקצבה
  payerName?: string;
  payerIdNumber?: string;
  isPayerForeignResident?: boolean;
}

// Helper function to format error messages
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.response?.data?.errors) {
    // Handle validation errors object from server
    const validationErrors = error.response.data.errors;
    const errorMessages: string[] = [];
    Object.values(validationErrors).forEach((value: any) => {
      if (Array.isArray(value)) {
        errorMessages.push(...value);
      } else {
        errorMessages.push(String(value));
      }
    });
    return errorMessages.join(', ');
  }
  if (error.message) {
    return error.message;
  }
  return 'אירעה שגיאה לא צפויה';
};

// Format date helper
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

class ClientsApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  async getAll(filter?: ClientFilterDto): Promise<ClientPagedResultDto> {
    const params = new URLSearchParams();
    
    if (filter) {
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }

    const response = await axios.get(
      `${API_BASE_URL}/clients?${params.toString()}`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getById(id: number): Promise<ClientDto> {
    const response = await axios.get(
      `${API_BASE_URL}/clients/${id}`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getUnassigned(): Promise<ClientDto[]> {
    const response = await axios.get(
      `${API_BASE_URL}/clients/unassigned`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getCaseManagerLoads(): Promise<Record<number, number>> {
    const response = await axios.get(
      `${API_BASE_URL}/clients/case-manager-loads`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async assignToCaseManager(assignment: ClientAssignmentDto): Promise<ClientDto> {
    const response = await axios.put(
      `${API_BASE_URL}/clients/${assignment.clientId}/assign`,
      assignment,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async bulkAssignToCaseManager(assignment: BulkClientAssignmentDto): Promise<ClientAssignmentResultDto> {
    const response = await axios.post(
      `${API_BASE_URL}/clients/bulk-assign`,
      assignment,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async transferClient(transfer: ClientTransferDto): Promise<ClientDto> {
    const response = await axios.put(
      `${API_BASE_URL}/clients/${transfer.clientId}/transfer`,
      transfer,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async updateMedicalInfo(clientId: number, medicalInfo: ClientMedicalInfoDto[]): Promise<ClientDto> {
    const response = await axios.put(
      `${API_BASE_URL}/clients/${clientId}/medical-info`,
      medicalInfo,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async updateClient(clientId: number, updateDto: UpdateClientDto): Promise<ClientDto> {
    const response = await axios.put(
      `${API_BASE_URL}/clients/${clientId}`,
      updateDto,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async getNationalInsurancePassword(clientId: number): Promise<string> {
    const response = await axios.get(
      `${API_BASE_URL}/clients/${clientId}/national-insurance-password`,
      this.getAuthHeaders()
    );
    return response.data.password;
  }

  async createClient(createDto: CreateClientDto): Promise<ClientDto> {
    const response = await axios.post(
      `${API_BASE_URL}/clients`,
      createDto,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async checkIdNumberExists(idNumber: string): Promise<{ exists: boolean }> {
    const response = await axios.get(
      `${API_BASE_URL}/clients/check-id-exists/${idNumber}`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async deleteClient(clientId: number): Promise<{ message: string }> {
    const response = await axios.delete(
      `${API_BASE_URL}/clients/${clientId}`,
      this.getAuthHeaders()
    );
    return response.data;
  }

  async revertToLead(clientId: number): Promise<{ leadId: number; message: string }> {
    const response = await axios.post(
      `${API_BASE_URL}/clients/${clientId}/revert-to-lead`,
      {},
      this.getAuthHeaders()
    );
    return response.data;
  }
}

export const clientsApi = new ClientsApiService();
export default clientsApi;